import { useParams } from 'next/navigation';

// Constants
import { UserRole } from '@shared/constants';
import ENV from '@shared/configs';

// Mocks
import { TimeOffRequest } from '@shared/mocks';

// Utils
import { getUserRole } from '@/utils';
import { formatDate, formatDateStringToMonthYear } from '@shared/utils';
import { isLunchRegistration, isPastDate, mergeActivitiesById, sortTrainingsByStatusAndDate } from '@/utils/employee';

// Types
import { ActionGroup, ActivityType } from '@shared/types';
import { EmployeeTraining, Employee, UserProfile, BaseEntityWebSocketResponse, EmployeeLunchRegistration, EmployeeRequestTimeOffData, EmployeeTrainingActivity, EmployeeActivityData } from '@/types';

// Providers
import { EmployeeContextType } from '@/providers';

// Hooks
import { useWebSocketMessage } from './useWebSocket';
type WebSocketMessageFromSpace = EmployeeTraining | {
  data: Employee;
} | UserProfile | {
  training: EmployeeTraining;
  activities: EmployeeTrainingActivity[];
};
interface TicketApproveRejectData {
  id: string;
  status: string;
  data: {
    start: string;
    end: string;
  };
  numberDayOff: number;
}
type WebSocketMessageFromFlask = BaseEntityWebSocketResponse<EmployeeLunchRegistration> | BaseEntityWebSocketResponse<TimeOffRequest & EmployeeRequestTimeOffData> | BaseEntityWebSocketResponse<TicketApproveRejectData>;
export const useEmployeeWebsocket = (props: EmployeeContextType) => {
  const {
    relationship,
    setTrainings,
    setInfo,
    setIsNotFound,
    setTimeOffRequests,
    setLunchRegistration,
    setActivities,
    fetchActivities
  } = props;
  const {
    username
  } = useParams();

  // Listen websocket message from Space API
  useWebSocketMessage<WebSocketMessageFromSpace>(async message => {
    if (!message?.channel || !message?.payload) return;
    switch (message.channel) {
      // Training-related activities
      case ActivityType.TRAINING_CREATE:
        {
          if ('user' in message.payload && !message.payload.user?.includes(username as string || '')) {
            return;
          }
          setTrainings(prev => sortTrainingsByStatusAndDate([...prev, message.payload as EmployeeTraining]));
          await fetchActivities();
          break;
        }
      case ActivityType.TRAINING_START:
      case ActivityType.TRAINING_COMPLETE:
      case ActivityType.TRAINING_PAUSE:
      case ActivityType.TRAINING_RESUME:
      case ActivityType.TRAINING_UPDATE:
      case ActivityType.TRAINING_REQUEST_FEEDBACK_PRESENTATION:
      case ActivityType.TRAINING_UNASSIGN_PARTICIPANT:
      case ActivityType.TRAINING_FEEDBACK_RECEIVED:
        {
          if ('user' in message.payload && !message.payload.user?.includes(username as string || '')) {
            return;
          }
          setTrainings(prev => sortTrainingsByStatusAndDate(prev.map(training => {
            const updatedTraining = message.payload as EmployeeTraining;
            if (training.id !== updatedTraining.id) {
              return training;
            }
            const {
              activities,
              participants,
              supporters,
              ...rest
            } = updatedTraining;

            // For TRAINING_FEEDBACK_RECEIVED, exclude participants to avoid overwriting avatar data
            const shouldIncludeParticipants = message.channel !== ActivityType.TRAINING_FEEDBACK_RECEIVED;
            return shouldIncludeParticipants ? {
              ...training,
              ...rest,
              supporters,
              participants
            } : {
              ...training,
              ...rest
            };
          })));

          // For other training updates, refresh activities except for supporter assignment and general updates
          if (message.channel !== ActivityType.TRAINING_UPDATE) {
            await fetchActivities();
          }
          break;
        }
      case ActivityType.TRAINING_ARCHIVE:
      case ActivityType.TRAINING_UNARCHIVE:
        {
          if ('user' in message.payload && !message.payload.user?.includes(username as string || '')) {
            return;
          }
          setTrainings(prev => sortTrainingsByStatusAndDate(prev.map(training => {
            const updatedTraining = message.payload as EmployeeTraining;
            const {
              archived
            } = updatedTraining;
            return training.id === (message.payload as EmployeeTraining).id ? {
              ...training,
              archived
            } : training;
          })));
          const {
            activities = []
          } = message.payload as EmployeeTraining;
          if (activities.length) {
            setActivities(prev => mergeActivitiesById(prev, activities));
          }
          break;
        }
      case ActivityType.TRAINING_ASSIGN_SUPPORTER:
      case ActivityType.TRAINING_UNASSIGN_SUPPORTER:
        {
          if (!('training' in message.payload)) return;
          const trainingData = message.payload.training as EmployeeTraining;
          if (trainingData?.user && !trainingData.user.includes(username as string || '')) {
            return;
          }
          setTrainings(prev => sortTrainingsByStatusAndDate(prev.map(training => {
            const {
              activities,
              supporters,
              id,
              ...rest
            } = trainingData;
            return training.id === id ? {
              ...training,
              ...rest,
              supporters
            } : training;
          })));
          if (message.channel === ActivityType.TRAINING_ASSIGN_SUPPORTER && relationship?.supporters?.length) {
            await fetchActivities();
          }
          break;
        }
      // Employee info-related activities
      case ActivityType.EMPLOYEE_UPDATE_INFO:
        {
          if (!('data' in message.payload)) return;
          const employeeInfoUpdate = (message.payload as {
            data: Employee;
          }).data;
          if (employeeInfoUpdate.info && employeeInfoUpdate.info.username === username) {
            const {
              birthday,
              ...rest
            } = employeeInfoUpdate.info;
            setInfo?.(prev => prev ? {
              ...prev,
              ...rest,
              birthday: birthday ? formatDateStringToMonthYear(formatDate(birthday)) : undefined
            } : undefined);
          }
          break;
        }
      case ActivityType.USER_ACTIVATE:
      case ActivityType.USER_DEACTIVATE:
        {
          if ('data' in message.payload) return;
          const userInfoUpdate = message.payload as UserProfile;
          const userRole = getUserRole();
          if (userInfoUpdate.username !== username) return;
          setInfo?.(prev => prev ? {
            ...prev,
            status: userInfoUpdate.status
          } : undefined);
          if (message.channel === ActivityType.USER_DEACTIVATE && userRole !== UserRole.ADMIN) {
            setIsNotFound?.(true);
          }
          break;
        }
      default:
        return;
    }
  });

  // Listen websocket message from Flask API
  useWebSocketMessage<WebSocketMessageFromFlask>(async message => {
    if (!message?.channel || !message?.payload?.data) return;
    switch (message.channel) {
      case ActivityType.TICKET_LUNCH_REGISTRATION:
      case ActivityType.TICKET_ON_BEHALF_LUNCH_REGISTRATION:
        {
          if (message.payload.by && !message.payload.by.includes(username as string)) return;
          if (!isLunchRegistration(message.payload.data)) return;
          setLunchRegistration(message.payload.data);
          setActivities(prev => [...prev, {
            id: message.payload.id || '',
            group: (message.payload.group || '') as ActionGroup,
            type: message.channel!,
            occurredAt: message.payload.occurredAt || '',
            by: message.payload.by,
            data: message.payload.data as EmployeeActivityData
          }]);
          break;
        }
      case ActivityType.TICKET_TIME_OFF:
      case ActivityType.TICKET_ON_BEHALF_TIME_OFF:
        {
          if (!message.payload.data) return;
          setActivities(prev => [...prev, {
            id: message.payload.id || '',
            group: (message.payload.group || '') as ActionGroup,
            type: message.channel!,
            occurredAt: message.payload.occurredAt || '',
            by: message.payload.by,
            data: message.payload.data as EmployeeActivityData
          }]);
          break;
        }
      case ActivityType.TICKET_REJECT:
      case ActivityType.TICKET_APPROVE:
        {
          const ticketData = message.payload.data as TicketApproveRejectData;
          if (isPastDate(ticketData.data.start)) {
            setTimeOffRequests(prevRequests => prevRequests + (ticketData.numberDayOff || 0));
          } else setTimeOffRequests(prevRequests => prevRequests);
          break;
        }
      default:
        return;
    }
  }, [], ENV.FLASK_API_ENDPOINT);
};